/**
 * Reading the driver's stop checklist, and the completions it sits beside.
 *
 * THE TWO ENDPOINTS. The load detail page asks for two records of the same
 * visit, because the API keeps them apart on purpose:
 *
 *   GET /api/loads/:id/pod       the completions, a bare array
 *   GET /api/loads/:id/checklist the trail, an object
 *
 * The checklist is a SIBLING of the pod route rather than a block inside it
 * (api/app/Routes/index.js says so beside the route), and the reason is worth
 * repeating on this side of the wire: a signed proof of delivery and a
 * best-practices checklist are different kinds of claim, and nesting the
 * second inside the first would invite a reader to treat them as one
 * artifact. This module keeps them apart for the same reason, and the page
 * fetches them separately.
 *
 * The checklist response already does the work a client would otherwise
 * guess at: it resolves each stop's template items, joins the driver's name
 * onto every event, and carries the sentence that says what the record is.
 * So the job here is normalisation and defence, not interpretation — every
 * shape that is not the documented one reads as "no checklist", never as a
 * crash and never as a wrong trail.
 */

function isObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** `pickup.count` / `seal-intact` -> a sentence, for an item with no label. */
function humanize(key) {
  const words = String(key)
    .replace(/^[a-z]+\./i, '')
    .replace(/[_.-]+/g, ' ')
    .trim();
  if (!words) return String(key);
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function usableKey(key) {
  if (typeof key === 'number' && Number.isFinite(key)) return String(key);
  return typeof key === 'string' && key ? key : null;
}

/**
 * The completions in a pod response body.
 *
 * The contract is a bare ARRAY and has been since spec 13
 * (ProofOfDeliveryController#loadPod ends in `ctx.body = completions.map(...)`).
 * An object carrying a `completions` array is read too — not because the API
 * sends one, but because losing every certificate on the page to a shape
 * change is the worst failure this client has available and the cheapest one
 * to defend against. Anything else is a proxied body or an older deployment,
 * and reads as no proof of delivery.
 */
export function readCompletions(body) {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body.completions)) return body.completions;
  return [];
}

/**
 * The checklist response, normalised.
 *
 * @returns {{version: (number|null),
 *   stops: Array<{stop_id: *, items: Array, events: Array}>}|null}
 *   null when the body is not a checklist response at all.
 */
export function readChecklist(body) {
  if (!isObject(body) || !Array.isArray(body.stops)) return null;

  const stops = body.stops
    .filter(isObject)
    .map(stop => {
      const stopId = stop.stop_id === undefined ? stop.id : stop.stop_id;
      if (stopId === null || stopId === undefined) return null;
      return {
        stop_id: stopId,
        items: normaliseItems(stop.items),
        events: (Array.isArray(stop.events) ? stop.events : []).filter(
          event => isObject(event) && usableKey(event.item_key)
        ),
      };
    })
    .filter(Boolean);

  const version = body.checklist_template_version;
  return {
    version: typeof version === 'number' ? version : null,
    stops,
  };
}

/**
 * The template items for a stop, `{ key, label }`, in the order the response
 * gave them: a checklist is a sequence, and reordering one would
 * misrepresent what the driver was asked to do first.
 */
function normaliseItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map(entry => {
      if (typeof entry === 'string') return { key: entry, label: humanize(entry) };
      if (!isObject(entry)) return null;
      const key = usableKey(entry.key !== undefined ? entry.key : entry.item_key);
      if (!key) return null;
      return { key, label: entry.label || entry.title || humanize(key) };
    })
    .filter(Boolean);
}

/**
 * One stop's half of the trail, or empty when the checklist knows nothing
 * about it.
 *
 * Stop ids are compared as strings: an id arrives as a number from the load
 * graph and can arrive as a string from a numeric column, and a trail that
 * vanished because of that would look exactly like a driver who ticked
 * nothing — the one failure here that reads as an answer rather than as a
 * fault.
 */
export function trailForStop(checklist, stopId) {
  const empty = { items: [], events: [] };
  if (!checklist || !Array.isArray(checklist.stops)) return empty;
  if (stopId === null || stopId === undefined) return empty;
  const found = checklist.stops.find(stop => String(stop.stop_id) === String(stopId));
  return found ? { items: found.items, events: found.events } : empty;
}
